import { animate, style, transition, trigger } from '@angular/animations';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { finalize, take } from 'rxjs/operators';
import { ModuleCard, ModuleCatalogService } from 'src/app/shared/services/module-catalog/module-catalog.service';
import { ThemeService } from 'src/app/shared/services/theme/theme.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { API_PATH } from 'src/environments/environment';
import { SettingsBranch, SettingsService } from '../settings/settings.service';
import { NotificationCrudIndex } from '../notifications-modal/crud-index/notification.component';
import { HomeCarouselImage, HomeCarouselImageService } from './home-carousel-image.service';
import { SettingsDialogComponent } from './settings-dialog/settings-dialog.component';

export type HomeLayoutMode = 'modules' | 'logo' | 'highlights';

@Component({
    selector: 'app-home-cards',
    templateUrl: './home-cards.component.html',
    styleUrls: ['./home-cards.component.sass'],
    animations: [
        trigger('carouselAnimation', [
            transition('void => active', [
                style({ opacity: 0, transform: 'scale(0.95) translateX(50px)' }),
                animate('600ms ease-in-out', style({ opacity: 1, transform: 'scale(1) translateX(0)' }))
            ]),
            transition('active => void', [
                animate('600ms ease-in-out', style({ opacity: 0, transform: 'scale(0.95) translateX(-50px)' }))
            ]),
            transition('inactive => active', [
                style({ opacity: 0, transform: 'scale(0.95) translateX(50px)' }),
                animate('600ms ease-in-out', style({ opacity: 1, transform: 'scale(1) translateX(0)' }))
            ]),
            transition('active => inactive', [
                animate('600ms ease-in-out', style({ opacity: 0, transform: 'scale(0.95) translateX(-50px)' }))
            ])
        ])
    ]
})
export class HomeCardsComponent implements OnInit, OnDestroy {
    menuCards: ModuleCard[] = [];
    carouselSlides: HomeCarouselImage[] = [];
    readonly carouselSlots = [0, 1];
    moduleSearch = '';
    darkMode = false;
    layoutMode: HomeLayoutMode = 'modules';
    branchLogoUrl = '';
    branchTitle = '';
    private readonly layoutStorageKey = '@wazir:home-card-layout';

    currentSlide = 0;
    carouselInterval: any;
    loading = false;

    /** Fileiras visíveis na grade antes da paginação. */
    readonly moduleRowsPerPage = 4;
    moduleColumns = 3;
    modulePageIndex = 0;

    constructor(
        private router: Router,
        public authService: AuthService,
        private dialog: MatDialog,
        private carouselService: HomeCarouselImageService,
        private themeService: ThemeService,
        private moduleCatalog: ModuleCatalogService,
        private settingsService: SettingsService
    ) { }

    ngOnInit(): void {
        this.darkMode = this.themeService.isDarkTheme();
        this.restoreLayoutMode();
        this.updateModuleColumns();
        this.initializeContent();
        this.loadBranchLogo();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.updateModuleColumns();
    }

    ngOnDestroy(): void {
        this.stopCarouselTimer();
    }

    get userFirstName(): string {
        const user = this.authService.getCurrentUser();
        const name = user?.firstName || user?.name || user?.email?.split('@')[0] || 'usuário';
        return name.toString().trim().split(/\s+/)[0];
    }

    get filteredMenuCards(): ModuleCard[] {
        return this.moduleCatalog.filter(this.menuCards, this.moduleSearch);
    }

    get modulePageSize(): number {
        return this.moduleRowsPerPage * this.moduleColumns;
    }

    get moduleTotalPages(): number {
        return Math.max(1, Math.ceil(this.filteredMenuCards.length / this.modulePageSize));
    }

    get currentModulePageIndex(): number {
        return Math.min(this.modulePageIndex, this.moduleTotalPages - 1);
    }

    get pagedMenuCards(): ModuleCard[] {
        const start = this.currentModulePageIndex * this.modulePageSize;
        return this.filteredMenuCards.slice(start, start + this.modulePageSize);
    }

    get modulePageNumbers(): number[] {
        return Array.from({ length: this.moduleTotalPages }, (_, index) => index);
    }

    get moduleRangeLabel(): string {
        const total = this.filteredMenuCards.length;
        if (!total) {
            return '0 módulos';
        }

        const start = this.currentModulePageIndex * this.modulePageSize + 1;
        const end = Math.min(start + this.modulePageSize - 1, total);
        return `${start}–${end} de ${total}`;
    }

    setModuleSearch(value: string): void {
        this.moduleSearch = value;
        this.modulePageIndex = 0;
    }

    goToModulePage(index: number): void {
        const lastPage = this.moduleTotalPages - 1;
        this.modulePageIndex = Math.min(Math.max(index, 0), lastPage);
    }

    updateModuleColumns(width: number = window.innerWidth): void {
        const columns = width <= 599 ? 1 : 3;
        if (columns === this.moduleColumns) {
            return;
        }

        this.moduleColumns = columns;
        this.modulePageIndex = 0;
    }

    toggleTheme(): void {
        this.darkMode = this.themeService.toggle();
    }

    get showsModuleCards(): boolean {
        return this.layoutMode === 'modules';
    }

    get showsBranchLogo(): boolean {
        return this.layoutMode === 'logo';
    }

    get showsHighlights(): boolean {
        return this.layoutMode !== 'logo';
    }

    get highlightsExpanded(): boolean {
        return this.layoutMode === 'highlights';
    }

    get layoutModeIcon(): string {
        if (this.layoutMode === 'logo') {
            return 'storefront';
        }
        if (this.layoutMode === 'highlights') {
            return 'panorama';
        }
        return 'grid_view';
    }

    get layoutModeLabel(): string {
        if (this.layoutMode === 'logo') {
            return 'Modo: logo da filial';
        }
        if (this.layoutMode === 'highlights') {
            return 'Modo: destaques';
        }
        return 'Modo: módulos';
    }

    setLayoutMode(mode: string): void {
        this.layoutMode = this.normalizeLayoutMode(mode);
        try {
            window.localStorage.setItem(this.layoutStorageKey, this.layoutMode);
        } catch {
            // Preferência visual; a tela segue mesmo sem armazenamento.
        }
        if (this.layoutMode === 'logo') {
            this.loadBranchLogo();
        }
    }

    restoreLayoutMode(): void {
        let stored = '';
        try {
            stored = window.localStorage.getItem(this.layoutStorageKey) || '';
        } catch {
            stored = '';
        }
        this.layoutMode = this.normalizeLayoutMode(stored);
    }

    normalizeLayoutMode(mode: string): HomeLayoutMode {
        if (mode === 'logo' || mode === 'highlights' || mode === 'modules') {
            return mode;
        }
        return 'modules';
    }

    formatBranchLogoUrl(logo: string): string {
        const value = (logo || '').trim();
        if (!value || /^https?:\/\//i.test(value) || value.startsWith('data:')) {
            return value;
        }
        return `${API_PATH}${value.replace(/^\//, '')}`;
    }

    applyBranchLogo(branch: Pick<SettingsBranch, 'title' | 'logo'> | null): void {
        this.branchTitle = (branch?.title || '').trim();
        this.branchLogoUrl = this.formatBranchLogoUrl(branch?.logo || '');
    }

    loadBranchLogo(): void {
        const listBranchOptions = this.authService?.listBranchOptions?.bind(this.authService);
        if (!listBranchOptions || !this.settingsService?.getBranch) {
            return;
        }

        listBranchOptions().pipe(take(1)).subscribe({
            next: (res) => {
                const branchId = res?.selectedBranchId;
                if (!branchId) {
                    this.loadActiveBranchLogo();
                    return;
                }

                this.settingsService.getBranch(branchId).pipe(take(1)).subscribe({
                    next: (branch) => this.applyBranchLogo(branch),
                    error: () => this.loadActiveBranchLogo(),
                });
            },
            error: () => this.loadActiveBranchLogo(),
        });
    }

    private loadActiveBranchLogo(): void {
        if (!this.settingsService?.getGlobalActiveBranch) {
            this.applyBranchLogo(null);
            return;
        }

        this.settingsService.getGlobalActiveBranch().pipe(take(1)).subscribe({
            next: (branch) => this.applyBranchLogo(branch),
            error: () => this.applyBranchLogo(null),
        });
    }

    /**
     * Inicializa o conteúdo da página
     */
    private initializeContent(): void {
        this.menuCards = this.moduleCatalog.getVisibleCards();
        this.loadCarouselImages();
        this.startCarouselTimer();
    }

    /**
     * Verifica se um card específico deve estar habilitado com base nas permissões
     */
    public canEnableCard(role: string): boolean {
        return this.moduleCatalog.canEnableCard(role);
    }

    /**
     * Carrega as imagens do carrossel
     */
    loadCarouselImages(): void {
        this.loading = true;
        this.carouselService.getActive()
            .pipe(finalize(() => this.loading = false))
            .subscribe(
                (images: HomeCarouselImage[]) => {
                    if (images && images.length > 0) {
                        this.processCarouselImages(images);
                    } else {
                        this.carouselSlides = [];
                    }
                },
                (error: any) => {
                    console.error('Erro ao carregar imagens do carrossel:', error);
                    this.carouselSlides = [];
                }
            );
    }

    /**
     * Processa as imagens do carrossel obtidas da API
     */
    private processCarouselImages(images: HomeCarouselImage[]): void {
        this.carouselSlides = images.sort((a, b) => a.order - b.order);

        this.carouselSlides.forEach(slide => {
            slide.imageUrl = this.carouselService.getFormattedImageUrl(slide.imageUrl);
        });
    }

    /**
     * Navega para uma rota específica
     */
    navigateTo(route: string): void {
        const card = this.menuCards.find(c => c.routerLink === route || c.action === route);

        if (!card) {
            console.warn(`Rota não encontrada: ${route}`);
            return;
        }

        if (this.shouldShowSoonBadge(card.role)) {
            return;
        }

        if (!this.canEnableCard(card.role)) {
            console.warn(`Usuário sem permissão para acessar: ${card.title}`);
            return;
        }

        if (card.action) {
            switch (card.action) {
                case 'openNotificationsModal':
                    this.ModalNotifications();
                    break;
                default:
                    console.warn(`Ação desconhecida para o card: ${card.action}`);
                    break;
            }
            return;
        }

        if (card.role === 'MENU_BIRTHDAYS') {
            this.router.navigate([route], { queryParams: { tab: 'birthdays' } });
        } else {
            this.router.navigate([route]);
        }
    }

    /**
     * Navegar para um link ao clicar em uma imagem do carrossel
     */
    navigateToSlideLink(index: number): void {
        const slide = this.carouselSlides[index];
        if (!slide || !slide.link || slide.link.trim() === '') {
            return;
        }

        if (slide.link.startsWith('http') || slide.link.startsWith('www')) {
            window.open(slide.link, '_blank');
        } else {
            this.router.navigate([slide.link]);
        }
    }

    /**
     * Abre o modal de configurações
     */
    openSettings(): void {
        const dialogRef = this.dialog.open(SettingsDialogComponent, {
            width: '35%',
            disableClose: false
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.refreshCarousel) {
                this.loadCarouselImages();
            }
        });
    }

    // Métodos para o carrossel
    startCarouselTimer(): void {
        this.carouselInterval = setInterval(() => {
            this.nextSlide();
        }, 5000);
    }

    stopCarouselTimer(): void {
        if (this.carouselInterval) {
            clearInterval(this.carouselInterval);
        }
    }

    resetCarouselTimer(): void {
        this.stopCarouselTimer();
        this.startCarouselTimer();
    }

    setCurrentSlide(index: number): void {
        this.currentSlide = index;
        this.resetCarouselTimer();
    }

    getSlideIndexForSlot(slot: number): number {
        if (!this.carouselSlides || slot >= this.carouselSlides.length) {
            return -1;
        }

        return (this.currentSlide + slot) % this.carouselSlides.length;
    }

    getSlideForSlot(slot: number): HomeCarouselImage | null {
        const index = this.getSlideIndexForSlot(slot);
        return index >= 0 ? this.carouselSlides[index] : null;
    }

    prevSlide(): void {
        this.currentSlide = this.currentSlide === 0 ?
            this.carouselSlides.length - 1 : this.currentSlide - 1;
        this.resetCarouselTimer();
    }

    nextSlide(): void {
        this.currentSlide = this.currentSlide === this.carouselSlides.length - 1 ?
            0 : this.currentSlide + 1;
    }

    /**
     * Verifica se um card deve mostrar o badge "EM BREVE".
     */
    public shouldShowSoonBadge(role: string): boolean {
        return this.moduleCatalog.shouldShowSoonBadge(role);
    }

    //NOTIFICATIONS
    public ModalNotifications() {
        const dialogRef = this.dialog.open(NotificationCrudIndex, {
            width: '40%',
            disableClose: false
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.refreshCarousel) {
                this.loadCarouselImages();
            }
        });
    }
}
